import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, faqs } from '@/mocks/movies';
import { useState } from 'react';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '天美麻花星空影视',
    url: window.location.origin,
    description: '提供高清流畅的免费在线影视观看服务。天美麻花星空影视免费观看，麻花星空影视高清免费在线播放。',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9odak4it35ii3jdm69g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg =
          (parsed as { meta?: { message?: string } })?.meta?.message ||
          (parsed as { message?: string })?.message ||
          responseText ||
          '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <section className="mb-14">
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">关于天美麻花星空影视</h1>
                <p className="text-foreground-400 text-sm md:text-base leading-relaxed mb-6 max-w-3xl">
                  天美麻花星空影视是一款致力于提供高清流畅免费影视观看体验的在线平台。我们汇聚了海量的电影、电视剧、综艺、动漫和纪录片资源，让每一位用户都能随时随地畅享精彩的视听盛宴。天美麻花星空影视免费观看，不缓冲不卡顿，极速流畅。
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                  <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                      <i className="ri-movie-2-line text-xl text-primary-400"></i>
                    </div>
                    <h3 className="text-white font-semibold mb-2">海量资源</h3>
                    <p className="text-foreground-500 text-sm">涵盖电影、电视剧、综艺、动漫、纪录片等多种类型</p>
                  </div>
                  <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-500/10">
                      <i className="ri-speed-up-line text-xl text-accent-400"></i>
                    </div>
                    <h3 className="text-white font-semibold mb-2">流畅播放</h3>
                    <p className="text-foreground-500 text-sm">高清不缓冲不卡顿，极速加载，畅享观影体验</p>
                  </div>
                  <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-500/10">
                      <i className="ri-shield-check-line text-xl text-secondary-400"></i>
                    </div>
                    <h3 className="text-white font-semibold mb-2">完全免费</h3>
                    <p className="text-foreground-500 text-sm">无需注册登录，无需付费，全部影视免费在线观看</p>
                  </div>
                </div>
              </section>

              <section className="mb-14" id="reputation">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-2">
                  <span className="w-1 h-6 rounded-full bg-primary-500"></span>
                  用户口碑
                </h2>
                <p className="text-foreground-500 text-sm mb-8">听听用户们怎么说</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {testimonials.map((t) => (
                    <div
                      key={t.id}
                      className="p-6 rounded-xl bg-white/[0.02] border border-white/5 hover:border-primary-500/20 transition-all duration-300"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-10 h-10 rounded-full object-cover"
                          loading="lazy"
                        />
                        <div>
                          <h3 className="text-white text-sm font-medium">{t.name}</h3>
                          <div className="flex items-center gap-0.5 mt-1">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <i
                                key={i}
                                className={`text-xs ${i < t.rating ? 'ri-star-fill text-primary-400' : 'ri-star-line text-foreground-700'}`}
                              ></i>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-foreground-400 text-sm leading-relaxed">{t.content}</p>
                      <p className="text-foreground-600 text-xs mt-3">{t.date}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14" id="faq">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-2">
                  <span className="w-1 h-6 rounded-full bg-accent-500"></span>
                  常见问题
                </h2>
                <p className="text-foreground-500 text-sm mb-8">关于使用天美麻花星空影视的常见疑问</p>
                <div className="flex flex-col gap-3">
                  {faqs.map((faq, index) => (
                    <details
                      key={index}
                      className="group rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all duration-300"
                    >
                      <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-white text-sm font-medium list-none">
                        {faq.question}
                        <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
                      </summary>
                      <div className="px-6 pb-4">
                        <p className="text-foreground-400 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              <section className="mb-14" id="contact">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-2">
                  <span className="w-1 h-6 rounded-full bg-secondary-500"></span>
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm mb-8">如有任何问题或建议，欢迎通过以下方式联系我们</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 flex-shrink-0">
                        <i className="ri-mail-line text-primary-400"></i>
                      </div>
                      <div>
                        <h3 className="text-white text-sm font-medium mb-1">电子邮箱</h3>
                        <p className="text-foreground-500 text-sm">contact@tianmeimahua.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500/10 flex-shrink-0">
                        <i className="ri-wechat-line text-accent-400"></i>
                      </div>
                      <div>
                        <h3 className="text-white text-sm font-medium mb-1">微信公众号</h3>
                        <p className="text-foreground-500 text-sm">天美麻花星空影视</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-500/10 flex-shrink-0">
                        <i className="ri-time-line text-secondary-400"></i>
                      </div>
                      <div>
                        <h3 className="text-white text-sm font-medium mb-1">服务时间</h3>
                        <p className="text-foreground-500 text-sm">周一至周日 9:00 - 22:00</p>
                      </div>
                    </div>
                  </div>

                  <form
                    data-readdy-form="true"
                    className="flex flex-col gap-4 p-6 rounded-xl bg-white/[0.02] border border-white/5"
                    onSubmit={handleSubmit}
                  >
                    <h3 className="text-white text-sm font-semibold mb-1">给我们留言</h3>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/10 text-white text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/10 text-white text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                    />
                    <textarea
                      name="message"
                      placeholder="请输入您的留言内容..."
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/10 text-white text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                    ></textarea>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                    />
                    <button
                      type="submit"
                      className="px-6 py-3 rounded-full bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-400 transition-all duration-300 whitespace-nowrap"
                    >
                      发送留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-green-400 text-xs">感谢您的留言，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-red-400 text-xs">{formError}</p>
                    )}
                  </form>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}